import React from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Context } from "../App";
import { deleteDesign, listMyDesigns, updateDesign } from "../services/designService";
import DesignCardGrid from "../components/designs/DesignCardGrid";
import Header from "../components/layout/Header";

export default function DesignManager() {
    const navigate = useNavigate();
    const { deconnect } = React.useContext(Context);
    const [designs, setDesigns] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    const loadDesigns = React.useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const data = await listMyDesigns();
            setDesigns(data);
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            setError("Erreur lors du chargement des designs.");
        } finally {
            setLoading(false);
        }
    }, [deconnect]);

    React.useEffect(() => {
        loadDesigns();
    }, [loadDesigns]);

    const handleDelete = async (design) => {
        const ok = window.confirm(`Supprimer le design "${design.title}" ?`);
        if (!ok) return;

        try {
            await deleteDesign(design._id);
            setDesigns((prev) => prev.filter((d) => d._id !== design._id));
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors de la suppression du design.");
        }
    };

    const handleEdit = (design) => navigate(`/design/${design._id}/edit`);
    const handlePreview = (design) => navigate(`/design/${design._id}/view`);
    const handlePublish = () => {
        alert("Action publier à implémenter plus tard.");
    };

    const handleRename = async (design) => {
        const nextTitle = window.prompt("Nouveau nom du design", design.title || "");
        if (!nextTitle) return;
        const trimmed = nextTitle.trim();
        if (!trimmed || trimmed === design.title) return;

        try {
            const sourceModelId = design?.model?._id || design?.model || null;
            const updated = await updateDesign(design._id, { ...design, title: trimmed }, sourceModelId);
            setDesigns((prev) => prev.map((d) => (d._id === design._id ? updated : d)));
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors du renommage du design.");
        }
    };

    return (
        <div>
            <Header title="Gestion des designs" />
            
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Design Manager
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Gérez vos designs créés à partir des modèles: visualisation, édition, suppression et publication.
                </Typography>

                <Stack direction="row" spacing={1.5}>
                    <Button variant="contained" href="/design/new">Nouveau design depuis modèle</Button>
                    <Button variant="text" onClick={loadDesigns}>Actualiser</Button>
                </Stack>

                <Box sx={{ mt: 2 }}>
                    {loading && (
                        <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loading && error && <Alert severity="error">{error}</Alert>}
                    {!loading && !error && (
                        <DesignCardGrid
                            designs={designs}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onPreview={handlePreview}
                            onPublish={handlePublish}
                            onRename={handleRename}
                        />
                    )}
                </Box>
            </Box>
        </div>
    );
}
